import React, { useMemo, useRef, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { Button, Modal, Page, Radio, alert } from 'equasion-lib';
import {
  FiAlertTriangle,
  FiBox,
  FiCheckCircle,
  FiClock,
  FiRotateCcw,
  FiShoppingBag,
  FiXCircle
} from 'react-icons/fi';
// The packing sheet's own size field and rules, so a correction is chosen exactly as at packing.
import PackageSizeField from '@/pages/SalesOrderShipping/PackageSizeField';
import { changePackageType, savedSizesFor } from '@/pages/SalesOrderShipping/packageSize';
import {
  type ShipPackage,
  type ShipPackageDraft,
  billableWeight,
  changedPackages,
  draftChanges,
  draftDimensions,
  draftOf,
  formatPounds,
  formatSize,
  packageTypeLabel,
  totalBillableWeight
} from '@/lib/shipPackages';
import './SalesOrderPackages.scss';

/** The server's stale-save refusal (SHIP_PACKAGES_STALE_MESSAGE) starts with this. */
const STALE_PREFIX = 'Someone else changed these box sizes';

const NOTE_MAX_LENGTH = 500;

const errorMessage = (e: any, fallback: string) =>
  e?.graphQLErrors?.[0]?.message || e?.message || fallback;

const formatWhen = (value: unknown) => {
  const date = new Date(value as any);
  if (Number.isNaN(date.getTime())) return '';
  return date.toLocaleString(undefined, {
    month: 'short',
    day: 'numeric',
    year: 'numeric',
    hour: 'numeric',
    minute: '2-digit'
  });
};

const formatDay = (value: unknown) => {
  const date = new Date(value as any);
  if (Number.isNaN(date.getTime())) return '';
  return date.toLocaleDateString(undefined, { month: 'short', day: 'numeric', year: 'numeric' });
};

const TypeIcon = ({ packageType }: { packageType?: string | null }) =>
  packageType === 'bag' ? <FiShoppingBag aria-hidden="true" /> : <FiBox aria-hidden="true" />;

/** "Box · 16 × 16 × 16 in". */
const describeSize = (packageType: string, dimensions: (number | string)[] | null) =>
  `${packageTypeLabel(packageType)} · ${dimensions ? formatSize(dimensions) : 'Size needed'}`;

type Saved = {
  changed: number;
  billableWeightBefore: number | null;
  billableWeightAfter: number | null;
  bundleNumber: string | null;
  bundleOrderCount: number;
};

/**
 * Box Sizes — correct the box a shipped order's packages went out in. Reached from the order's
 * Documents page. Only the size changes: the packages, their weights, the order's status and the
 * shipping label all stay as they are (see libs/services/src/order/ship-package-edits.ts).
 */
const SalesOrderPackages = ({
  salesOrder,
  shippingDimensionOptions,
  save,
  reload
}: {
  salesOrder: any;
  shippingDimensionOptions: unknown;
  save: (variables: {
    expectedRevision: number;
    packages: { index: number; packageType: string; dimensions: number[]; customSize: boolean }[];
    note?: string | null;
  }) => Promise<any>;
  reload: () => Promise<any>;
}) => {
  const navigate = useNavigate();
  const {
    id: orderId,
    vendorSalesOrderNumber,
    vendorStatus,
    metadata,
    trackingCodes,
    shippingBundle,
    shipPackages,
    shipPackagesRevision = 0,
    shipPackagesEditable,
    shipPackagesEditBlockedReason,
    shipPackageChanges
  } = salesOrder || {};
  const packages: ShipPackage[] = shipPackages || [];

  // The size being edited per package index, only for packages the operator has touched.
  const [drafts, setDrafts] = useState<Record<number, ShipPackageDraft | undefined>>({});
  // The package open in the sheet, and its value when it was opened (Cancel puts it back).
  const [openIndex, setOpenIndex] = useState<number | null>(null);
  const [openedWith, setOpenedWith] = useState<ShipPackageDraft | undefined>(undefined);
  // Every size error in the sheet shows once Done is pressed, as packing's Save does.
  const [sheetErrorsShown, setSheetErrorsShown] = useState(false);
  const [note, setNote] = useState('');
  const [confirming, setConfirming] = useState(false);
  const [saving, setSaving] = useState(false);
  const savingRef = useRef(false);
  const [stale, setStale] = useState<string | null>(null);
  const [reloading, setReloading] = useState(false);
  const [saved, setSaved] = useState<Saved | null>(null);
  const [leaveConfirm, setLeaveConfirm] = useState(false);

  const orderLabel = `SO #${vendorSalesOrderNumber || ''}`;
  const editable = !!shipPackagesEditable;
  const valueFor = (pkg: ShipPackage) => drafts[pkg.index] ?? draftOf(pkg, shippingDimensionOptions);
  const changes = useMemo(() => changedPackages(packages, drafts), [packages, drafts]);
  const invalidIndexes = packages
    .filter((pkg) => drafts[pkg.index] && !draftDimensions(drafts[pkg.index] as ShipPackageDraft))
    .map((pkg) => pkg.index);
  const hasChanges = changes.length > 0;
  const canSave = editable && hasChanges && invalidIndexes.length === 0 && !saving && !stale;

  const billableBefore = totalBillableWeight(
    packages.map((pkg) => ({ packageType: pkg.packageType, dimensions: pkg.dimensions, weight: pkg.weight }))
  );
  const billableAfter = totalBillableWeight(
    packages.map((pkg) => {
      const value = valueFor(pkg);
      return {
        packageType: value.type,
        dimensions: draftDimensions(value) ?? pkg.dimensions,
        weight: pkg.weight
      };
    })
  );

  // A shipped bundle is one carton recorded on every member: a change here is saved on all of them.
  const bundleSiblings =
    shippingBundle?.status === 'shipped'
      ? (shippingBundle.orders || []).filter((order) => order.id !== orderId)
      : [];

  const backToDocuments = () => navigate(`/sales-order-documents/${orderId}`);
  const requestLeave = () => (hasChanges ? setLeaveConfirm(true) : backToDocuments());

  const openPackage = (pkg: ShipPackage) => {
    if (!editable) return;
    setOpenedWith(drafts[pkg.index]);
    setSheetErrorsShown(false);
    setOpenIndex(pkg.index);
  };
  const setDraft = (index: number, value: ShipPackageDraft | undefined) =>
    setDrafts((current) => ({ ...current, [index]: value }));
  const closeSheet = ({ revert }: { revert: boolean }) => {
    if (openIndex === null) return;
    const pkg = packages.find((p) => p.index === openIndex);
    const draft = drafts[openIndex];
    if (revert) setDraft(openIndex, openedWith);
    else if (draft && !draftDimensions(draft)) {
      // Not usable yet: say what is missing instead of closing (packing's Save does the same).
      setSheetErrorsShown(true);
      return;
    }
    // Choosing the recorded size again is no change at all.
    else if (pkg && draft && !draftChanges(pkg, draft)) setDraft(openIndex, undefined);
    setOpenIndex(null);
    setOpenedWith(undefined);
  };
  const undo = (index: number) => setDraft(index, undefined);

  const openPkg = openIndex === null ? null : packages.find((p) => p.index === openIndex) || null;
  const openValue = openPkg ? valueFor(openPkg) : null;
  const openDimensions = openValue ? draftDimensions(openValue) : null;

  const handleReload = async () => {
    setReloading(true);
    try {
      await reload();
      setDrafts({});
      setStale(null);
      setConfirming(false);
    } catch (e) {
      alert('Could not reload', errorMessage(e, 'Try again.'), 6, 'error');
    } finally {
      setReloading(false);
    }
  };

  const handleSave = async () => {
    if (savingRef.current || !canSave) return;
    savingRef.current = true;
    setSaving(true);
    try {
      const { data } = await save({
        expectedRevision: shipPackagesRevision,
        packages: changes,
        note: note.trim() || null
      });
      const result = data?.salesOrderShipPackagesUpdate;
      setDrafts({});
      setNote('');
      setConfirming(false);
      setSaved({
        changed: result?.changedPackageCount ?? changes.length,
        billableWeightBefore: result?.billableWeightBefore ?? null,
        billableWeightAfter: result?.billableWeightAfter ?? null,
        bundleNumber: result?.bundleNumber ?? null,
        bundleOrderCount: result?.bundleOrderIds?.length ?? 0
      });
      alert(
        'Box sizes saved',
        `${orderLabel} is still ${vendorStatus === 'completed' ? 'Completed' : 'Shipped'}.`,
        4,
        'success'
      );
      // The mutation returns the saved order; refetch anyway so the page reads the server's truth.
      reload().catch(() => undefined);
    } catch (e) {
      const message = errorMessage(e, 'Could not save the box sizes.');
      setConfirming(false);
      if (message.startsWith(STALE_PREFIX)) setStale(message);
      else alert('Could not save', message, 8, 'error');
    } finally {
      savingRef.current = false;
      setSaving(false);
    }
  };

  const shippedDate = metadata?.shippedDate ? formatDay(metadata.shippedDate) : null;
  const tracking = (trackingCodes || []).map((code) => code?.number).filter(Boolean);
  const history = shipPackageChanges || [];

  if (!salesOrder) {
    return (
      <Page className="sales-order-packages action-page">
        <div className="action-top-bar">
          <div className="order-details">
            <h1>Box Sizes</h1>
          </div>
          <FiXCircle role="button" aria-label="Close" onClick={() => navigate('/sales-orders')} />
        </div>
        <div className="action-page-body">
          <div className="packages-notice is-blocked" role="status">
            <FiAlertTriangle aria-hidden="true" />
            <p>This order could not be found for the selected company.</p>
          </div>
        </div>
      </Page>
    );
  }

  return (
    <Page className="sales-order-packages action-page">
      <div className="action-top-bar">
        <div className="order-details">
          <h1>{orderLabel}</h1>
          <h2>Box Sizes</h2>
        </div>
        <FiXCircle role="button" aria-label="Close" onClick={requestLeave} />
      </div>

      <div className="action-page-body">
        <section className="packages-context" aria-label="Shipment">
          <span className="status-pill">
            {vendorStatus === 'completed' ? 'Completed' : vendorStatus === 'shipped' ? 'Shipped' : vendorStatus}
          </span>
          <span>
            {[shippedDate && `Shipped ${shippedDate}`, metadata?.shippingMethodName, tracking[0]]
              .filter(Boolean)
              .join(' · ')}
            {tracking.length > 1 && ` +${tracking.length - 1}`}
          </span>
        </section>

        {editable ? (
          <p className="packages-lead">
            Tap a package to correct the box it went out in. The order stays{' '}
            {vendorStatus === 'completed' ? 'Completed' : 'Shipped'}, and the shipping label is not
            voided or reprinted.
          </p>
        ) : (
          <div className="packages-notice is-blocked" role="status">
            <FiAlertTriangle aria-hidden="true" />
            <p>{shipPackagesEditBlockedReason || 'Box sizes cannot be changed on this order.'}</p>
          </div>
        )}

        {bundleSiblings.length > 0 && (
          <div className="packages-notice is-bundle">
            <FiBox aria-hidden="true" />
            <p>
              Shipped as one carton with{' '}
              {bundleSiblings.map((order, i) => (
                <React.Fragment key={order.id}>
                  {i > 0 && (i === bundleSiblings.length - 1 ? ' and ' : ', ')}
                  <strong>SO #{order.vendorSalesOrderNumber}</strong>
                </React.Fragment>
              ))}{' '}
              (bundle {shippingBundle.bundleNumber}). A change here is saved on all{' '}
              {bundleSiblings.length + 1} orders.
            </p>
          </div>
        )}

        {stale && (
          <div className="packages-notice is-stale" role="alert">
            <FiAlertTriangle aria-hidden="true" />
            <div>
              <p>{stale}</p>
              <Button
                type="secondary"
                fullWidth
                disabled={reloading}
                {...{ onClick: handleReload }}
              >
                {reloading ? 'Reloading…' : 'Reload Latest Sizes'}
              </Button>
            </div>
          </div>
        )}

        {saved && !hasChanges && (
          <div className="packages-notice is-saved" role="status">
            <FiCheckCircle aria-hidden="true" />
            <p>
              Saved {saved.changed === 1 ? '1 new box size' : `${saved.changed} new box sizes`}
              {saved.bundleOrderCount > 0 &&
                ` on this order and ${saved.bundleOrderCount} more in bundle ${saved.bundleNumber}`}
              .
              {saved.billableWeightBefore !== null &&
                saved.billableWeightAfter !== null &&
                saved.billableWeightBefore !== saved.billableWeightAfter &&
                ` Billable weight ${saved.billableWeightBefore} → ${saved.billableWeightAfter} lb; the carrier bills by the parcel it measures, so expect any adjustment on its invoice.`}
            </p>
          </div>
        )}

        <ol className="package-list" aria-label="Packages">
          {packages.map((pkg) => {
            const value = valueFor(pkg);
            const invalid = invalidIndexes.includes(pkg.index);
            const changed = !!drafts[pkg.index] && !invalid && draftChanges(pkg, value);
            const valueDimensions = draftDimensions(value);
            const newBillable = valueDimensions
              ? billableWeight(value.type, valueDimensions, pkg.weight)
              : null;
            return (
              <li
                key={pkg.index}
                className={['package-card', changed && 'is-changed', invalid && 'is-invalid', editable && 'is-editable']
                  .filter(Boolean)
                  .join(' ')}
              >
                <button
                  type="button"
                  className="package-card__main"
                  disabled={!editable}
                  onClick={() => openPackage(pkg)}
                  aria-label={`Package ${pkg.number}: ${describeSize(value.type, valueDimensions)}${
                    editable ? '. Tap to change the box size.' : ''
                  }`}
                >
                  <span className="package-card__kicker">
                    Package {pkg.number}
                    {changed && <span className="package-card__flag">Changed</span>}
                    {invalid && <span className="package-card__flag is-problem">Size needed</span>}
                  </span>
                  <span className="package-card__size">
                    <TypeIcon packageType={value.type} />
                    {describeSize(value.type, valueDimensions)}
                    {value.sizeMode === 'custom' && <span className="package-card__tag">Custom</span>}
                  </span>
                  {(changed || invalid) && (
                    <span className="package-card__was">
                      Was {describeSize(pkg.packageType, pkg.dimensions)}
                    </span>
                  )}
                  <span className="package-card__weights">
                    {formatPounds(pkg.weight)} weighed ·{' '}
                    {changed && pkg.billableWeight !== newBillable ? (
                      <>
                        billable {formatPounds(pkg.billableWeight)} →{' '}
                        <strong>{formatPounds(newBillable)}</strong>
                      </>
                    ) : (
                      <>billable {formatPounds(newBillable)}</>
                    )}
                  </span>
                </button>
                {changed || invalid ? (
                  <button
                    type="button"
                    className="package-card__action"
                    onClick={() => undo(pkg.index)}
                    aria-label={`Undo the change to package ${pkg.number}`}
                  >
                    <FiRotateCcw aria-hidden="true" />
                    Undo
                  </button>
                ) : (
                  editable && (
                    <span className="package-card__action is-hint" aria-hidden="true">
                      Change
                    </span>
                  )
                )}
              </li>
            );
          })}
        </ol>

        {editable && hasChanges && (
          <label className="packages-note">
            <span>Reason (optional)</span>
            <textarea
              value={note}
              maxLength={NOTE_MAX_LENGTH}
              rows={2}
              placeholder="e.g. Packed in a larger box than was entered"
              onChange={(e) => setNote(e.target.value)}
            />
          </label>
        )}

        {editable && (
          <div className="packages-save-bar">
            <p className="packages-save-summary" aria-live="polite">
              {hasChanges
                ? `${changes.length === 1 ? '1 package' : `${changes.length} packages`} changed${
                    billableBefore !== null && billableAfter !== null && billableBefore !== billableAfter
                      ? ` · billable ${billableBefore} → ${billableAfter} lb`
                      : ''
                  }`
                : 'No changes yet'}
            </p>
            <Button
              type="primary"
              fullWidth
              disabled={!canSave}
              {...{ onClick: () => setConfirming(true) }}
            >
              {saving ? 'Saving…' : 'Save Box Sizes'}
            </Button>
          </div>
        )}

        <section className="packages-history" aria-labelledby="packages-history-title">
          <h3 id="packages-history-title">
            <FiClock aria-hidden="true" /> Change history
          </h3>
          {history.length === 0 ? (
            <p className="packages-history__empty">No box sizes have been changed since it shipped.</p>
          ) : (
            <ol>
              {history.map((entry) => (
                <li key={entry.id} className="packages-history__entry">
                  <div className="packages-history__meta">
                    <strong>{entry.userName || 'Unknown user'}</strong>
                    <time dateTime={new Date(entry.at).toISOString()}>{formatWhen(entry.at)}</time>
                  </div>
                  <ul>
                    {entry.changes.map((change) => (
                      <li key={change.index}>
                        <span className="packages-history__package">Package {change.number}</span>
                        <span className="packages-history__sizes">
                          <span className="is-old">{describeSize(change.from.packageType, change.from.dimensions)}</span>
                          <span aria-hidden="true"> → </span>
                          <span className="visually-hidden"> changed to </span>
                          <span className="is-new">{describeSize(change.to.packageType, change.to.dimensions)}</span>
                        </span>
                        {change.from.billableWeight != null &&
                          change.to.billableWeight != null &&
                          change.from.billableWeight !== change.to.billableWeight && (
                            <span className="packages-history__billable">
                              Billable {change.from.billableWeight} → {change.to.billableWeight} lb
                            </span>
                          )}
                      </li>
                    ))}
                  </ul>
                  {entry.note && <p className="packages-history__note">“{entry.note}”</p>}
                  {entry.sourceOrderNumber && (
                    <p className="packages-history__source">
                      Changed from SO #{entry.sourceOrderNumber}
                      {entry.bundleNumber ? ` (bundle ${entry.bundleNumber})` : ''}
                    </p>
                  )}
                  {!entry.sourceOrderNumber && entry.bundleNumber && (
                    <p className="packages-history__source">Saved on every order in bundle {entry.bundleNumber}</p>
                  )}
                </li>
              ))}
            </ol>
          )}
        </section>
      </div>

      {/* The package sheet: the same picker as at packing. */}
      <Modal
        className="shipping-package-modal packages-sheet"
        modalOpen={!!openPkg}
        setModalOpen={(open) => {
          if (!open) closeSheet({ revert: !openDimensions });
        }}
      >
        {openPkg && openValue && (
          <div className="packages-sheet__body">
            <h3>Package {openPkg.number}</h3>
            <p className="packages-sheet__recorded">
              Shipped as {describeSize(openPkg.packageType, openPkg.dimensions)}
            </p>
            <div className="package-sheet__heading">Package type</div>
            <Radio
              form={false}
              options={[
                ['box', 'Box'],
                ['bag', 'Poly Bag']
              ]}
              value={openValue.type}
              defaultValue={openValue.type}
              onChange={({ value }) =>
                setDraft(openPkg.index, changePackageType(openValue, value, shippingDimensionOptions))
              }
            />
            <PackageSizeField
              // A fresh field per package and type, as at packing.
              key={`${openPkg.index}-${openValue.type}`}
              pkg={openValue}
              savedSizes={savedSizesFor(shippingDimensionOptions, openValue.type)}
              onChange={(patch) => setDraft(openPkg.index, { ...openValue, ...patch })}
              showAllErrors={sheetErrorsShown}
              onSubmit={() => closeSheet({ revert: false })}
            />
            <p className="packages-sheet__weight">
              Weight stays {formatPounds(openPkg.weight)} as weighed · billable{' '}
              {formatPounds(
                openDimensions ? billableWeight(openValue.type, openDimensions, openPkg.weight) : null
              )}
            </p>
            <Button
              type="primary"
              fullWidth
              {...{ onClick: () => closeSheet({ revert: false }) }}
            >
              Done
            </Button>
            <Button type="secondary" fullWidth {...{ onClick: () => closeSheet({ revert: true }) }}>
              Cancel
            </Button>
          </div>
        )}
      </Modal>

      {/* Save confirmation: what changes, and what does not. */}
      <Modal
        className="packages-confirm-modal"
        modalOpen={confirming}
        setModalOpen={(open) => {
          if (!open && !savingRef.current) setConfirming(false);
        }}
      >
        <div className="packages-confirm-modal__body">
          <strong>Save new box sizes?</strong>
          <ul className="packages-confirm-modal__changes">
            {changes.map((change) => {
              const pkg = packages.find((p) => p.index === change.index);
              return (
                <li key={change.index}>
                  <span>Package {change.index + 1}</span>
                  <span>
                    {pkg ? formatSize(pkg.dimensions) : ''} → {formatSize(change.dimensions)}
                  </span>
                </li>
              );
            })}
          </ul>
          <ul className="packages-confirm-modal__facts">
            <li>
              {orderLabel} stays {vendorStatus === 'completed' ? 'Completed' : 'Shipped'}.
            </li>
            {bundleSiblings.length > 0 && (
              <li>
                Also saved on{' '}
                {bundleSiblings.map((order) => `SO #${order.vendorSalesOrderNumber}`).join(', ')}, which
                shipped in the same carton.
              </li>
            )}
            <li>The shipping label is not voided or reprinted, and nothing is sent to the client.</li>
            {billableBefore !== null && billableAfter !== null && billableBefore !== billableAfter && (
              <li>
                Billable weight goes from {billableBefore} to {billableAfter} lb. The carrier bills by the
                parcel it measures.
              </li>
            )}
          </ul>
          <div className="packages-confirm-modal__actions">
            <Button type="primary" fullWidth disabled={saving} {...{ onClick: handleSave }}>
              {saving ? 'Saving…' : 'Save'}
            </Button>
            <Button
              type="secondary"
              fullWidth
              disabled={saving}
              {...{ onClick: () => setConfirming(false) }}
            >
              Keep Editing
            </Button>
          </div>
        </div>
      </Modal>

      {/* Leaving with unsaved changes. */}
      <Modal
        className="packages-confirm-modal"
        modalOpen={leaveConfirm}
        setModalOpen={(open) => {
          if (!open) setLeaveConfirm(false);
        }}
      >
        <div className="packages-confirm-modal__body">
          <strong>Discard your changes?</strong>
          <p>The new box sizes have not been saved.</p>
          <div className="packages-confirm-modal__actions">
            <Button type="primary" fullWidth {...{ onClick: backToDocuments }}>
              Discard
            </Button>
            <Button type="secondary" fullWidth {...{ onClick: () => setLeaveConfirm(false) }}>
              Keep Editing
            </Button>
          </div>
        </div>
      </Modal>
    </Page>
  );
};

export default SalesOrderPackages;
